// SPDX-License-Identifier: UNLICENSED
// Slice B/C — Research Transparency (WS-7 / WS-1)
//
// Single source of truth for the three user-facing transparency
// strings that appear on Landing.js, AnalyzeLanding.js,
// AIInsightsPanel.js, TransparencyPanel.jsx and the central
// ResearchTransparency page. Consumers pass:
//
//   * `t` from `useTranslation()`
//   * the backend-issued `methodology_version` (from `ws7_metadata`)
//   * an optional `correction_notice` object
//     ({ status?, label?, as_of? } — `status` is the canonical
//     backend `CorrectionStatus` enum value)
//
// and receive the exact copy to render.  No component may hardcode
// methodology / conflicts / corrections wording locally — this file
// is the canonical mapping the spec requires.
//
// The methodology label is DERIVED from the real, existing
// `methodology_version` identifier ("stockaily.methodology.v1").
// The label maps back to the actual implemented method (weighted
// blend of DCF + sector P/E + five fundamental factors, per the
// Slice-C preflight trace of `poc/valuation_engine.compute_fair_value`).
// We do NOT invent a v2.

// Canonical CorrectionStatus values (mirrored from
// backend/services/ws7_metadata.py::CorrectionStatus). Used to map
// the backend-supplied `correction_notice.status` to a localized
// label. Unknown/future statuses fall back to a preserved backend
// label OR the honest "correction published" localized fallback —
// the notice is NEVER silently hidden.
export const CORRECTION_STATUS_I18N_KEY = {
  metadata_corrected: "research_transparency.correction_status_metadata_corrected",
  data_corrected: "research_transparency.correction_status_data_corrected",
  methodology_updated: "research_transparency.correction_status_methodology_updated",
  material_research_updated: "research_transparency.correction_status_material_research_updated",
  superseded: "research_transparency.correction_status_superseded",
  withdrawn: "research_transparency.correction_status_withdrawn",
};

// The deterministic English `label` strings the current backend
// derives from `status` via a lookup table
// (`backend/services/ws7_correction.py::public_correction_notice`).
// When the backend `label` matches one of these boilerplate values
// AND `status` is known, we render ONLY the localized status label
// (English boilerplate would otherwise leak into a DE UI). When the
// backend `label` DIFFERS from the boilerplate we treat it as
// editorial free-form content and preserve it verbatim next to the
// localized status.
const BOILERPLATE_LABEL_FOR_STATUS = {
  metadata_corrected: "Metadata updated",
  data_corrected: "Underlying data corrected",
  methodology_updated: "Methodology updated",
  material_research_updated: "Research updated",
  superseded: "Superseded by a newer analysis",
  withdrawn: "Withdrawn",
};

/** Long-form label for the transparency panel body. */
export function methodologyLabel(t, methodology_version) {
  // Canonical identifier issued by the backend today.  Every real
  // analysis carries "stockaily.methodology.v1".  When (later) the
  // valuation engine adds a v2, add a new mapping here rather than
  // editing components.
  if (methodology_version === "stockaily.methodology.v1") {
    return t("research_transparency.methodology_long_v1");
  }
  // Genuinely absent methodology metadata → honest missing-data state.
  return t("research_transparency.methodology_missing");
}

/** Short label for tight rows (Landing status line, AI Insights row). */
export function methodologyShortLabel(t, methodology_version) {
  if (methodology_version === "stockaily.methodology.v1") {
    return t("research_transparency.methodology_short_v1");
  }
  return t("research_transparency.methodology_missing");
}

/** Conflicts of interest — factual Stockaily disclosure. */
export function conflictsLabel(t /*, hint */) {
  // Business fact: Stockaily receives no compensation from the
  // analysed company. Do not introduce broader independence claims.
  return t("research_transparency.conflicts_no_compensation");
}

/**
 * Corrections — a real `correction_notice` object ALWAYS wins over
 * the "no corrections published" fallback.  The renderer must never
 * hide a real correction.
 *
 * Precedence (documented for the compliance report):
 *   1. **Canonical localized status label** — if
 *      `correction_notice.status` is a known `CorrectionStatus`
 *      enum value, we render the localized status label from
 *      `research_transparency.correction_status_*`. A DE UI never
 *      sees the English boilerplate the backend derives from
 *      `status`.
 *   2. **Editorial free-form description** — if
 *      `correction_notice.description` is present it is preserved
 *      verbatim (the backend does not localize editorial content).
 *      Not part of the current payload; supported forward-compatibly
 *      so that a future editorial workflow can add it without
 *      touching the frontend.
 *   3. **Editorial label** — the backend also emits a `label`. When
 *      it equals the deterministic English boilerplate for the
 *      current `status` we drop it (redundant English noise). When
 *      it DIFFERS from the boilerplate we treat it as editorial and
 *      preserve it verbatim next to the localized status label.
 *   4. **Date** — a valid `as_of` is appended as "(as of / Stand …)"
 *      regardless of which head was chosen. An invalid date is
 *      silently omitted — never fabricated.
 *   5. **Unknown status fallback** — a notice with an unknown status
 *      still surfaces: the editorial `description` or `label` is
 *      preserved if present; otherwise the localized
 *      "Correction published / Korrektur veröffentlicht" copy is
 *      shown so the notice remains visible.
 *   6. **True no-correction state** — only when NONE of the above
 *      yields a string do we fall through to
 *      "No corrections published / Keine Korrekturen veröffentlicht".
 */
export function correctionsLabel(t, correction_notice) {
  if (correction_notice && typeof correction_notice === "object") {
    const status = typeof correction_notice.status === "string"
      ? correction_notice.status.trim()
      : "";
    const rawLabel = typeof correction_notice.label === "string"
      ? correction_notice.label.trim()
      : "";
    const description = typeof correction_notice.description === "string"
      ? correction_notice.description.trim()
      : "";
    const asOfRaw = correction_notice.as_of;

    // 1) Canonical status → localized headline.
    const i18nKey = status ? CORRECTION_STATUS_I18N_KEY[status] : null;
    let head = null;
    const editorialParts = [];

    if (i18nKey) {
      head = t(i18nKey);
      // 3) Only preserve `label` when it is genuinely editorial —
      // i.e. NOT the deterministic English boilerplate the backend
      // derives from `status`. This prevents the English boilerplate
      // from leaking into a DE UI while still preserving genuinely
      // editorial content if it appears.
      const boilerplate = BOILERPLATE_LABEL_FOR_STATUS[status];
      if (rawLabel && rawLabel !== boilerplate) {
        editorialParts.push(rawLabel);
      }
    } else if (rawLabel) {
      // Unknown status but the backend sent a label — preserve it as
      // the head (never silently drop editorial content).
      head = rawLabel;
    } else if (status || description || asOfRaw) {
      // 5) Unknown status without editorial label — honest fallback
      // so the notice is still visible.
      head = t("research_transparency.correction_status_unknown");
    }

    // 2) Free-form editorial description — preserved verbatim
    // regardless of which head was chosen.
    if (description) {
      editorialParts.push(description);
    }

    if (head) {
      const editorial = editorialParts.length
        ? ` — ${editorialParts.join(" · ")}`
        : "";
      let suffix = "";
      if (asOfRaw) {
        const d = new Date(asOfRaw);
        if (!isNaN(d.getTime())) {
          suffix =
            ` (${t("research_transparency.corrections_as_of_prefix")} ${d.toLocaleDateString(
              undefined,
              { year: "numeric", month: "short", day: "numeric" }
            )})`;
        }
      }
      return `${head}${editorial}${suffix}`;
    }
  }
  // 6) Genuine "no correction" state.
  return t("research_transparency.corrections_none");
}
